import React, { useEffect } from 'react';
import { GameArea, GamePlayerList, GameChat, GameTilesLeft } from './features/game/Board';
import { Lobby } from './features/game/Lobby';
import { PlayerList } from './features/room/PlayerList';
import { TableListNew, TableListStarted } from './features/room/TableList';
import { Chat } from './features/room/Chat';
import { getSelected, getSubTitleAndPath } from './features/view/viewSlice';
import { getTitle } from './features/view/Title';
import { getLocalPlayer, getID, getRoomTabState } from './features/room/roomSlice';
import { inGame, getLoading } from './features/game/gameSlice';
import { getInited } from './features/init/initSlice';
import { LoadingSpinner } from './features/loading/LoadingSpinner';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend  } from 'react-dnd-touch-backend'
import { useTranslation } from 'react-i18next';
import { Tabs } from './Tabs';
import { getBasePath } from './util/path';
import ReactGA from 'react-ga';
import { isMobile } from 'react-device-detect';
import classNames from "classnames";
import { preload } from './util/sound';
import { RoomList } from './features/rooms/RoomList';
import { getMainTabState, getRoom } from './features/rooms/roomsSlice';
import { DialogHandler } from './DialogHandler';
import { ConnectionStatus } from './components/ConnectionStatus';
import { isAndroidApp } from './util/app';
import { storeState } from './util/storage';
import useResizeObserver from "use-resize-observer";

import './App.css';

const GameTabDesktop = () => {
  const dispatch = useDispatch();
  const { ref, height = 1 } = useResizeObserver();

  let new_width = 582;
  if (height > 300) {
    new_width = Math.min(((height - 52) * 0.85) + 10, 582);
  }
  return (
    <div className="gameTab" ref={ref} style={{gridTemplateColumns: new_width+'px auto'}}>
      <div className="column">
        <GameArea/>
      </div>
      <div className="column">
        <div style={{position: 'absolute', right: '10px'}}><button onClick={e => dispatch({ type: 'action/quitgame' })} style={{float: 'right'}}>X</button></div>
        <div><img src="images/logo.svg" className="logo" alt="Logo"/></div>
        <div className="box">
          <GamePlayerList/>
          <GameTilesLeft/>
        </div>
        <GameChat/>
      </div>
    </div>
  );
};

const GameTab = () => {
  const localPlayer = useSelector(getLocalPlayer, shallowEqual);
  const inGame_ = useSelector(inGame);
  const loading = useSelector(getLoading);

  if (inGame_) {
    if (loading) {
      return <LoadingSpinner />;
    }

    if (isMobile) {
      return (
        <div className="gameTab">
          <div className="box">
            <GamePlayerList/>
            <GameTilesLeft/>
          </div>
          <GameArea/>
          <GameChat/>
        </div>
      );
    }

    return <GameTabDesktop />;
  }

  if (localPlayer.table === null) {
    return <LoadingSpinner />;
  }
  return <Lobby/>;
};

const RoomTab = () => {
  const state = useSelector(getRoomTabState);
  if (state !== 2) {
    return <LoadingSpinner />;
  }

  if (isMobile) {
    return (
      <div className="roomTab">
        <TableListNew />
        <PlayerList />
        <TableListStarted />
        <Chat/>
      </div>
    );
  }
  return (
    <div className="roomTab">
      <div className="column">
        <Chat/>
      </div>
      <div className="column">
        <PlayerList />
      </div>
      <div className="column">
        <TableListNew />
        <TableListStarted />
      </div>
    </div>
  );
};

const RoomHeader = () => {
  const roomID = useSelector(getID);
  const room = useSelector(state => getRoom(state, roomID), shallowEqual);
  return (
    <div className="header contentArea">
      <div className="logo"><img src="images/logo.svg" alt="Logo"/></div>
      <div style={{flex: 2}}></div>
      <div className="mascot"><img src={'images/rooms/'+room.filename} alt="Maskot"/></div>
    </div>
  );
};

const MainTab = () => {
  const state = useSelector(getMainTabState);
  if (state < 2) {
    return <LoadingSpinner />;
  }

  return (
    <div className="mainTab">
      <RoomList />
    </div>
  );
};

const MainHeader = () => {
  return (
    <div className="header contentArea">
      <div className="logo"><img src="images/logo.svg" alt="Logo"/></div>
      <div style={{flex: 5}}></div>
    </div>
  );
};

const Metadata = () => {
  const { t } = useTranslation();
  const [subtitle, subpath, state, url] = useSelector(getSubTitleAndPath);

  const subtitleStr = getTitle(t, subtitle.type, subtitle.args);
  const title = subtitleStr+' | Betapet'

  useEffect(() => {
    const currentState = window.history.state;
    document.title = title;
    if (shallowEqual(currentState, state)) {
      return;
    }
    window.history.replaceState(state, title, url);
    storeState(state);
  }, [state, title, url]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      const path = getBasePath()+subpath;
      ReactGA.set({ page: path });
      ReactGA.pageview(path);
    }
  }, [subpath]);

  return null;
};

const App = () => {
  const inited = useSelector(getInited);
  const tab = useSelector(getSelected);

  const content = (() => {
    if (!inited) {
      return (
        <div className="body">
          <LoadingSpinner />
        </div>
      );
    }

    switch (tab) {
      case 'room':
        return (
          <>
            <ConnectionStatus/>
            <RoomHeader/>
            <div className="body">
              <RoomTab />
            </div>
          </>
        );
      case "game":
        return (
          <>
            <ConnectionStatus/>
            <div className="body">
              <GameTab />
            </div>
          </>
        );
      default:
        return (
          <>
            <MainHeader/>
            <div className="body">
              <MainTab />
            </div>
          </>
        );
    }
  })();

  const metadata = inited ? <Metadata/> : null;

  const cls = classNames({
    'isMobile': isMobile,
    'isAndroidApp': isAndroidApp,
  });

  useEffect(() => {
    let theme = 'light';
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        theme = 'dark';
      }

      // Add listener if the theme changes
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
        const newTheme = e.matches ? "dark" : "light";
        document.documentElement.setAttribute('data-theme', newTheme);
      });
    }
    document.documentElement.setAttribute('data-theme', theme);

    const preloadSound = (e) => {
      preload();
    }

    document.addEventListener('click', preloadSound, { once: true })
    return () => {
      document.removeEventListener('click', preloadSound);
    }
  }, []);

  const backend = isMobile ? TouchBackend : HTML5Backend;

  return (
    <DndProvider backend={backend}>
      <div className={cls}>
        <DialogHandler>
          <div className="App">
            <Tabs/>
            {content}
            <div style={{height: '10px', borderTop: '1px solid var(--border-color)'}}></div>
            {metadata}
          </div>
        </DialogHandler>
      </div>
    </DndProvider>
  );
};

export default App;
